import { useEffect, useState } from "react";
import { FaGavel, FaCheckCircle, FaSave, FaBalanceScale, FaUniversity, FaHome, FaUserFriends, FaCar, FaBriefcase, FaHeartbeat, FaGlobe, FaMoneyBill, FaBuilding } from "react-icons/fa";
import { GiHandcuffs, GiContract } from "react-icons/gi";
import { MdBusinessCenter, MdRealEstateAgent } from "react-icons/md";
import API_BASE_URL from "../config";
import { useAuth } from "../context/AuthContext";
import { getPracticeCategories } from "../constants/practiceAreas";
import "../styles/SolicitorPracticeArea.css";

export default function SolicitorPracticeArea() {
  const { user, token, login } = useAuth();

  /* ======================
     STATE
  ====================== */
  const [practiceCategories, setPracticeCategories] = useState({});
  const [selected, setSelected] = useState(user?.practiceAreas || []);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Icon mapping for practice areas
  const categoryIcons = {
    "Criminal Law": <GiHandcuffs size={20} />,
    "Civil Law": <FaBalanceScale size={20} />,
    "Corporate Law": <MdBusinessCenter size={20} />,
    "Family Law": <FaUserFriends size={20} />,
    "Property Law": <FaHome size={20} />,
    "Intellectual Property": <FaUniversity size={20} />,
    "Tax Law": <FaMoneyBill size={20} />,
    "Employment Law": <FaBriefcase size={20} />,
    "Immigration Law": <FaGlobe size={20} />,
    "Personal Injury": <FaHeartbeat size={20} />,
    "Banking & Finance": <FaBuilding size={20} />,
    "Real Estate": <MdRealEstateAgent size={20} />,
    "Contract Law": <GiContract size={20} />,
    "Motor Vehicle": <FaCar size={20} />
  };

  const getCategoryIcon = (category) => {
    return categoryIcons[category] || <FaGavel size={20} />;
  };

  /* ======================
     LOAD PRACTICE AREAS
  ====================== */
  useEffect(() => {
    const loadPracticeAreas = async () => {
      try {
        const data = await getPracticeCategories();
        setPracticeCategories(data || {});
      } catch (err) {
        console.error("Failed to load practice areas", err);
      }
    };

    loadPracticeAreas();
  }, []);

  /* ======================
     TOGGLE CATEGORY
  ====================== */
  const toggleCategory = (category) => {
    setSelected((prev) =>
      prev.some((c) => c.category === category)
        ? prev.filter((c) => c.category !== category)
        : [...prev, { category, areas: [] }]
    );
  };

  /* ======================
     TOGGLE AREA
  ====================== */
  const toggleSubArea = (category, area) => {
    setSelected((prev) =>
      prev.map((item) =>
        item.category === category
          ? {
            ...item,
            areas: item.areas.includes(area)
              ? item.areas.filter((a) => a !== area)
              : [...item.areas, area]
          }
          : item
      )
    );
  };

  /* ======================
     SAVE PRACTICE AREAS
  ====================== */
  const savePracticeAreas = async () => {
    if (selected.length === 0) {
      setMessage({ type: "error", text: "Please select at least one practice area" });
      return;
    }

    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/auth/solicitor/practice-areas`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ practiceAreas: selected })
        }
      );

      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: "error", text: data.message || "Failed to save practice areas" });
      } else {
        login(token, data.user);
        setMessage({ type: "success", text: "Practice areas updated successfully" });

        // Clear success message after 3 seconds
        setTimeout(() => setMessage({ type: "", text: "" }), 3000);
      }
    } catch {
      setMessage({ type: "error", text: "Server error occurred" });
    } finally {
      setLoading(false);
    }
  };

  /* ======================
     SELECT ALL AREAS IN CATEGORY
  ====================== */
  const selectAllInCategory = (category) => {
    const areas = practiceCategories[category] || [];
    setSelected((prev) =>
      prev.map((item) =>
        item.category === category
          ? { ...item, areas }
          : item
      )
    );
  };

  /* ======================
     CLEAR ALL AREAS IN CATEGORY
  ====================== */
  const clearAllInCategory = (category) => {
    setSelected((prev) =>
      prev.map((item) =>
        item.category === category
          ? { ...item, areas: [] }
          : item
      )
    );
  };

  /* ======================
     UI
  ====================== */
  return (
    <div className="solicitor-container">
      {/* Header */}
      <div className="solicitor-header-card">
        <div className="solicitor-header-content">
          <div>
            <h1 className="solicitor-main-title">Practice Areas</h1><br />
            <p className="solicitor-subtitle">Select your legal expertise and specializations</p>
          </div>
        </div>
      </div>

      {/* Message Alert */}
      {message.text && (
        <div className={`solicitor-message solicitor-message-${message.type}`}>
          <div className="solicitor-message-content">
            {message.type === "success" ? <FaCheckCircle size={18} /> : "!"}
            <span>{message.text}</span>
          </div>
          <button
            className="solicitor-message-close"
            onClick={() => setMessage({ type: "", text: "" })}
          >
            ×
          </button>
        </div>
      )}

      {/* Stats Summary */}
      <div className="solicitor-stats-summary">
        <div className="solicitor-stat-card">
          <div className="solicitor-stat-icon">
            <FaGavel size={20} />
          </div>
          <div>
            <div className="solicitor-stat-number">{selected.length}</div>
            <div className="solicitor-stat-label">Categories Selected</div>
          </div>
        </div>
        <div className="solicitor-stat-card">
          <div className="solicitor-stat-icon">
            <FaBriefcase size={20} />
          </div>
          <div>
            <div className="solicitor-stat-number">
              {selected.reduce((total, cat) => total + cat.areas.length, 0)}
            </div>
            <div className="solicitor-stat-label">Specializations</div>
          </div>
        </div>
      </div>

      {/* Practice Areas Grid */}
      <div className="solicitor-areas-grid">
        {Object.entries(practiceCategories).map(([category, areas]) => {
          const selectedCategory = selected.find(
            (c) => c.category === category
          );
          const selectedCount = selectedCategory?.areas.length || 0;
          const totalCount = areas.length;

          return (
            <div key={category} className="solicitor-category-card">
              {/* Category Header */}
              <div className="solicitor-category-header">
                <div className="solicitor-category-title-wrapper">
                  <div className="solicitor-category-icon">
                    {getCategoryIcon(category)}
                  </div>
                  <div>
                    <h3 className="solicitor-category-title">{category}</h3>
                    <div className="solicitor-category-subtitle">
                      {selectedCount} of {totalCount} areas selected
                    </div>
                  </div>
                </div>
                <div className="solicitor-category-actions">
                  <button
                    className="solicitor-action-small"
                    onClick={() => selectAllInCategory(category)}
                    disabled={!selectedCategory}
                  >
                    Select All
                  </button>
                  <button
                    className="solicitor-action-small solicitor-action-clear"
                    onClick={() => clearAllInCategory(category)}
                    disabled={!selectedCategory || selectedCount === 0}
                  >
                    Clear All
                  </button>
                </div>
              </div>

              {/* Category Selection */}
              <div className="solicitor-category-selection">
                <label className="solicitor-category-checkbox">
                  <input
                    type="checkbox"
                    className="solicitor-category-checkbox-input"
                    checked={!!selectedCategory}
                    onChange={() => toggleCategory(category)}
                  />
                  <span className="solicitor-checkbox-custom"></span>
                  <span className="solicitor-category-label">
                    Select all {category.toLowerCase()} areas
                  </span>
                </label>
              </div>

              {/* Sub Areas List */}
              {selectedCategory && (
                <div className="solicitor-subareas-container">
                  <div className="solicitor-subareas-grid">
                    {areas.map((area) => (
                      <label key={area} className="solicitor-subarea-checkbox">
                        <input
                          type="checkbox"
                          className="solicitor-subarea-input"
                          checked={selectedCategory.areas.includes(area)}
                          onChange={() => toggleSubArea(category, area)}
                        />
                        <span className="solicitor-subarea-checkbox-custom">
                          {selectedCategory.areas.includes(area) && (
                            <FaCheckCircle className="solicitor-subarea-selected-icon" />
                          )}
                        </span>
                        <span className="solicitor-subarea-label">{area}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Progress Bar */}
              {selectedCategory && totalCount > 0 && (
                <div className="solicitor-progress-container">
                  <div className="solicitor-progress-bar">
                    <div
                      className="solicitor-progress-fill"
                      style={{ width: `${(selectedCount / totalCount) * 100}%` }}
                    ></div>
                  </div>
                  <div className="solicitor-progress-text">
                    {selectedCount}/{totalCount} selected
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="solicitor-actions-section">
        <button
          className="solicitor-save-button"
          onClick={savePracticeAreas}
          disabled={loading}
        >
          <FaSave className="solicitor-button-icon" />
          <span>{loading ? "Saving..." : "Save Practice Areas"}</span>
          {loading && <div className="solicitor-button-spinner"></div>}
        </button>

        {selected.length > 0 && (
          <div className="solicitor-selected-summary">
            <FaCheckCircle className="solicitor-summary-icon" />
            <span>
              {selected.length} category{selected.length !== 1 ? 's' : ''} selected
            </span>
          </div>
        )}
      </div>
    </div>
  );
}